import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  liveArtistsState,
  paginationModelState,
  toastState,
} from '../../state/RecoilState';
import {
  Box,
  Typography,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  SelectChangeEvent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import { Artist, ArtistTestimonial } from '../../@types/api';
import { useGetLiveArtists } from '../../hooks/useGetLiveArtists';
import RestUtilities from '../../services/RestUtilities';
import { DataGrid } from '@mui/x-data-grid';
import { Edit } from '@mui/icons-material';

const formControlSxProps = { minWidth: '240px', mt: 2, mr: 1 };
const textAreaProps = { my: 1, mt: 2 };

const Testimonials: React.FC = () => {
  const setToast = useSetRecoilState(toastState);
  const [data, setData] = useState<ArtistTestimonial[] | undefined>(undefined);
  const [artistId, setArtistId] = useState(-1);
  const [selectedTestimonial, setSelectedTestimonial] = useState<
    ArtistTestimonial | undefined
  >(undefined);
  const artists = useRecoilValue(liveArtistsState);
  const [paginationModel, setPaginationModel] =
    useRecoilState(paginationModelState);

  useGetLiveArtists();

  useEffect(() => {
    const fetchData = async () => {
      const res = await RestUtilities.get(
        `api/testimonials/getById/${artistId}`
      );
      if (res.ok) {
        setData(res.content as ArtistTestimonial[]);
      }
    };
    if (artistId > 0 && !data) {
      fetchData();
      setToast('Data loaded');
    }
  }, [artistId, data, setToast]);

  const handleArtistChange = (event: SelectChangeEvent<unknown>) => {
    setArtistId(Number(event.target.value));
    setData(undefined);
  };

  const handleUpdateTestimonial = (
    field: keyof ArtistTestimonial,
    value: string
  ) => {
    const updated = {
      ...selectedTestimonial,
    } as ArtistTestimonial;
    updated[field] = value as never;
    setSelectedTestimonial(updated);
  };

  const updateTestimonial = async () => {
    if (!selectedTestimonial || !data) return;
    const res = await RestUtilities.put(
      `api/feedback/UpdateTestimonial`,
      selectedTestimonial
    );
    if (res.ok) {
      data?.forEach((t) => {
        if (t.testimonialId === selectedTestimonial.testimonialId) {
          t.bookingId = selectedTestimonial.bookingId;
          t.orderIndex = selectedTestimonial.orderIndex;
          t.testimonialName = selectedTestimonial.testimonialName;
          t.testimonialText = selectedTestimonial.testimonialText;
        }
      });
      setData([...data]);
      setSelectedTestimonial(undefined);
      setToast('testimonial updated');
    } else {
      setToast('testimonial update failed');
    }
  };

  const deleteTestimonial = async () => {
    if (!selectedTestimonial) return;
    const res = await RestUtilities.delete(
      `api/feedback/DeleteTestimonial/${selectedTestimonial.testimonialId}`
    );
    if (res.ok) {
      var newData = data?.filter(
        (t) => t.testimonialId !== selectedTestimonial.testimonialId
      );
      setData(newData);
      setSelectedTestimonial(undefined);
      setToast('testimonial deleted');
    } else {
      setToast('testimonial delete failed');
    }
  };

  return (
    <Box>
      <Box my={1} display='flex' justifyContent={'space-between'}>
        <Typography variant='h5'>Artist Testimonials</Typography>
        <Box>
          <FormControl size='small' sx={formControlSxProps}>
            <InputLabel id='artist-select-label'>Artist</InputLabel>
            <Select
              labelId='artist-select-label'
              id='artist-select'
              label='Artist'
              value={artistId}
              onChange={handleArtistChange}
              variant='outlined'
              size='small'
            >
              <MenuItem value={-1}>-- Select --</MenuItem>
              {artists.map((a: Artist) => (
                <MenuItem value={a.artistId} key={a.artistId}>
                  {a.artistName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Box>
        {data && (
          <DataGrid
            rows={data.toSorted((t1, t2) => {
              return (
                t1.orderIndex - t2.orderIndex ||
                t2.testimonialId - t1.testimonialId
              );
            })}
            getRowId={(row) => row.testimonialId}
            columns={[
              {
                headerName: 'ID',
                field: 'testimonialId',
                sortable: true,
                width: 70,
              },
              {
                field: 'actions',
                headerName: 'Actions',
                width: 60,
                sortable: false,
                renderCell: (params) => (
                  <div>
                    <IconButton
                      onClick={(e) => setSelectedTestimonial(params.row)}
                    >
                      <Edit />
                    </IconButton>
                  </div>
                ),
              },
              {
                headerName: 'BookingID',
                field: 'bookingId',
                type: 'number',
                sortable: true,
                width: 90,
              },
              {
                headerName: 'Name',
                field: 'testimonialName',
                type: 'string',
                sortable: true,
                width: 250,
              },
              {
                headerName: 'Comment',
                field: 'testimonialText',
                type: 'string',
                sortable: true,
                width: 700,
              },
              {
                headerName: 'Order',
                field: 'orderIndex',
                type: 'number',
                sortable: true,
                width: 70,
              },
            ]}
            getRowHeight={() => 'auto'}
            pageSizeOptions={[25, 50, 100, 250]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
          />
        )}
      </Box>
      <Dialog
        open={!!selectedTestimonial}
        onClose={() => setSelectedTestimonial(undefined)}
      >
        <DialogTitle>
          Edit Testimonial: {selectedTestimonial?.testimonialId}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ width: '500px', pt: 2 }}>
            <FormControl sx={formControlSxProps}>
              <TextField
                required
                id='bookingId'
                label='BookingId (if known)'
                value={selectedTestimonial?.bookingId}
                type='number'
                onChange={(event) =>
                  handleUpdateTestimonial('bookingId', event.target.value)
                }
                size='small'
              />
            </FormControl>
            <FormControl sx={formControlSxProps}>
              <TextField
                required
                id='clientName'
                label='ClientName (if no booking ID)'
                value={selectedTestimonial?.testimonialName}
                onChange={(event) =>
                  handleUpdateTestimonial('testimonialName', event.target.value)
                }
                size='small'
              />
            </FormControl>
            <FormControl sx={formControlSxProps}>
              <TextField
                required
                id='orderIndex'
                label='Order'
                type='number'
                value={selectedTestimonial?.orderIndex}
                onChange={(event) =>
                  handleUpdateTestimonial('orderIndex', event.target.value)
                }
                size='small'
              />
            </FormControl>
            <TextField
              multiline
              id='artist-comment'
              label={`Comments`}
              value={selectedTestimonial?.testimonialText ?? ''}
              fullWidth
              margin='normal'
              onChange={(event) => {
                handleUpdateTestimonial('testimonialText', event.target.value);
              }}
              InputProps={{
                rows: 5,
                multiline: true,
                inputComponent: 'textarea',
              }}
              variant='outlined'
              sx={textAreaProps}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Box
            sx={{ width: '100%' }}
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <Button
              variant='contained'
              color='primary'
              onClick={() => setSelectedTestimonial(undefined)}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              color='warning'
              onClick={() => updateTestimonial()}
            >
              Update
            </Button>
            <Button
              variant='contained'
              color='error'
              onClick={() => deleteTestimonial()}
            >
              Delete
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default Testimonials;
